import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EventProvider, useEventContext } from './contexts/EventContext';
import Calendar from './pages/Calendar';

import { HashRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';

import { PCSAppBar } from './components/AppBar';
import { Box, Button, CssBaseline, Grid, ThemeProvider, Typography } from '@mui/material';
import { createAppTheme } from './theme';
import { ThemeModeProvider, useThemeModeContext } from './contexts/ThemeModeContext';
import { EventModal } from './components/eventModal/EventModal';
import { BranchInfo } from './components/BranchInfo';
import { LoadingState } from './components/shared/LoadingState';
import { AuthenticationState } from './monad/AuthenticationState';
import { HttpClienthProvider } from './contexts/HttpClientContext';
import { useTranslation } from 'react-i18next';
import AdministerBannersPage from './pages/AdministerBannersPage';
import NotificationTestPage from './pages/NotificationTestPage';
import NotifyEventPage from './pages/NotifyEventPage';

const ThemedApp = ({ children }: { children: React.ReactNode }) => {
  const { resolvedMode } = useThemeModeContext();
  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <ThemeModeProvider>
      <ThemedApp>
        <HashRouter>
          <AuthProvider>
            <HttpClienthProvider>
              <EventProvider>
                <PCSAppBar />
                <NotificationOpener />
                <Routes>
                  <Route path="/" element={<Calendar />} />
                  <Route path="/admin/banners" element={<AdministerBannersPage />} />
                  <Route path="/callback" element={<SignInCallback />} />
                  <Route path="/admin/notifications" element={<NotificationTestPage />} />
                  <Route path="/admin/notify-event" element={<NotifyEventPage />} />
                </Routes>
                <EventModal />
                <BranchInfo />
              </EventProvider>
            </HttpClienthProvider>
          </AuthProvider>
        </HashRouter>
      </ThemedApp>
    </ThemeModeProvider>
  );
};

const NotificationOpener = () => {
  const eventsContext = useEventContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingEventId, setPendingEventId] = useState<string | null>(null);
  const lastHandledRef = useRef<string | null>(null);

  const consumeEventId = useCallback((eventId: string) => {
    if (!eventId || lastHandledRef.current === eventId) return;
    lastHandledRef.current = eventId;

    setPendingEventId(eventId);
    eventsContext.fetchEventModels();

    const params = new URLSearchParams(location.search);
    if (params.has("eventId")) {
      params.delete("eventId");
      const remaining = params.toString();
      navigate(
        { pathname: location.pathname, search: remaining ? `?${remaining}` : "" },
        { replace: true },
      );
    }
  }, [eventsContext, navigate, location.pathname, location.search]);

  useEffect(() => {
    const eventId = new URLSearchParams(location.search).get("eventId");
    if (eventId) consumeEventId(eventId);
  }, [location.search, consumeEventId]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (evt: MessageEvent) => {
      const d = evt.data;
      if (d && d.type === "OPEN_EVENT" && typeof d.eventId === "string") {
        lastHandledRef.current = null;
        consumeEventId(d.eventId);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [consumeEventId]);

  useEffect(() => {
    if (!eventsContext.eventModalOpen) {
      lastHandledRef.current = null;
    }
  }, [eventsContext.eventModalOpen]);

  useEffect(() => {
    if (!pendingEventId) return;
    eventsContext.events.match({
      success: (data) => {
        if (data.some((e) => e.eventId === pendingEventId)) {
          eventsContext.openEventModal(pendingEventId);
        } else {
          console.warn("Event from notification not found:", pendingEventId);
        }
        setPendingEventId(null);
      },
      error: () => { setPendingEventId(null); },
    });
  }, [eventsContext.events, pendingEventId, eventsContext]);

  return null;
};

const SignInCallback = () => {
  const authContext = useAuthContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loginFailed, setLoginFailed] = useState(false);

  //authContext.setUser(AuthenticationState.loggingIn());

  useEffect(() => {
    authContext.signinCallback()
      .then((user) => {
        console.log("Login success", user);
        authContext.setUser(AuthenticationState.loggedIn(user!));
        navigate("/");
      }).catch((error) => {
        console.log("Error logging in in callback", error);
        authContext.setUser(AuthenticationState.loggedOut());
        setLoginFailed(true);
      });

    return () => {
      setLoginFailed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (!loginFailed) {
    return <LoadingState label={t("app.logging_in")} />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="80vh"
    >
      <Typography variant="h4" gutterBottom>
        {t("app.login_failed")}
      </Typography>
      <Grid container spacing={2}>
        <Grid>
          <Button variant='contained' onClick={() => authContext.login()}>{t("app.try_again")}</Button>
        </Grid>
        <Grid>
          <Button variant='outlined' onClick={() => navigate("/")}>{t("app.cancel")}</Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default App;